import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { CreatePlaybookForm, NewPlaybookTile } from "../components/CreatePlaybook";
import { Mark } from "../components/Mark";
import { PlaybookTile } from "../components/PlaybookTile";
import { InlineNote } from "../components/primitives";
import { api } from "../lib/api";
import { duration, money } from "../lib/format";
import { typicalByPlaybook, type Typical } from "../lib/typical";
import type { Options, Playbook } from "../lib/types";

/**
 * The first thing after signing in: pick the piece of work.
 *
 * This used to be the page a *stranger* saw, with the tiles linking to
 * sign-in. Sign-in is the landing page now, so the tiles moved behind the
 * guard and became what they always described — a choice, made by somebody who
 * is already allowed to make it.
 *
 * **The mark draws itself here, with its line.** It was taken off for a while
 * — the page somebody opens every working day, opening on the question at the
 * left edge — and the page lost the thing that says whose it is. It is back,
 * large and centred with both headlines under it, drawn once per visit: it
 * mounts once, and the application bar above it still never animates.
 *
 * Each tile goes to that playbook's own form. The form differs per playbook —
 * an industry scan asks for an industry, a memo review will not start without
 * the memo — which is why the choice comes first rather than being a dropdown
 * on one shared page.
 */
const TILE_ORDER = [
  "cdd",
  "review-cim",
  "company-profile",
  "benchmarking",
  "industry-scan",
  "market-model",
  "key-questions",
  "storyline",
];

export function Choose() {
  const playbooks = useQuery({ queryKey: ["playbooks"], queryFn: api.playbooks });
  const options = useQuery({ queryKey: ["options"], queryFn: api.options });
  // Shared with the Engagements page's cache. What a playbook usually costs
  // is read off runs that finished, so it is a number the system has.
  const runs = useQuery({ queryKey: ["runs"], queryFn: api.runs });
  // On the model a run started from a tile would get — a median across
  // models is a figure for none of them.
  const defaults = useMemo(() => {
    const provider = options.data?.providers.find(
      (p) => p.key === options.data?.default_provider,
    );
    return provider ? { provider: provider.key, model: provider.default_model } : undefined;
  }, [options.data]);
  const typical = useMemo(
    () => (defaults ? typicalByPlaybook(runs.data ?? [], defaults) : new Map<string, Typical>()),
    [runs.data, defaults],
  );

  // A playbook of your own starts from the last tile under Other.
  const [creating, setCreating] = useState(false);

  const ordered = [...(playbooks.data ?? [])].sort((a, b) => {
    const rank = (key: string) => {
      const index = TILE_ORDER.indexOf(key);
      // An operator's own playbook has no place in the list and goes last,
      // rather than silently ahead of the shipped ones.
      return index === -1 ? TILE_ORDER.length : index;
    };
    return rank(a.key) - rank(b.key);
  });

  return (
    <div>
      <header className="flex flex-col items-center pb-12 pt-4 text-center sm:pt-8">
        <Mark size={140} animate />
        {/* The mark's own line, rising once after the drawing completes. */}
        <span className="mark-caption label mt-3">Connecting the dots</span>
        <h1 className="mt-8 text-3xl font-light tracking-tight text-ink sm:text-4xl">
          What do you want to do?
        </h1>
        <p className="mt-4 max-w-read text-base leading-relaxed text-ink-2">
          Every claim in the deliverable carries a finding, every finding
          carries its sources, and every source is checked against the pages
          that were actually retrieved.
        </p>
      </header>

      {playbooks.isLoading && (
        <p className="text-center text-sm text-muted">Loading the workflows…</p>
      )}
      {playbooks.isError && (
        <InlineNote tone="critical" className="justify-center">
          The workflow list could not be loaded. The service may be starting up.
        </InlineNote>
      )}

      {/*
        Grouped by the job the person has in mind, from each playbook's own
        `category:` — twelve tiles in one grid was a wall with nothing to
        choose by (#124). The groups and their order are the server's
        vocabulary; a playbook that declares none is listed under Other, and
        so is every playbook somebody writes — after the tile that writes one.
      */}
      <div className="space-y-12 pb-16">
        {playbooks.data && groupsOf(ordered, options.data).map((group) => (
          <section key={group.key} aria-labelledby={`group-${group.key}`}>
            <h2
              id={`group-${group.key}`}
              className="mb-4 border-b border-line pb-2 text-lg font-normal text-ink"
            >
              {group.label}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {group.books.map((playbook) => (
                <PlaybookTile
                  key={playbook.key}
                  playbook={playbook}
                  to={`/new/${encodeURIComponent(playbook.key)}`}
                  cost={costLine(playbook, typical.get(playbook.key), options.data)}
                />
              ))}
              {group.key === "other" && (
                <NewPlaybookTile open={creating} onToggle={() => setCreating((v) => !v)} />
              )}
            </div>
            {group.key === "other" && creating && (
              <CreatePlaybookForm onDone={() => setCreating(false)} />
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

interface Group {
  key: string;
  label: string;
  books: Playbook[];
}

function groupsOf(books: Playbook[], options: Options | undefined): Group[] {
  const vocabulary = options?.playbook_categories ?? [];
  const groups: Group[] = vocabulary.map((c) => ({ key: c.key, label: c.label, books: [] }));
  const other: Group = { key: "other", label: "Other", books: [] };
  for (const book of books) {
    (groups.find((g) => g.key === book.category) ?? other).books.push(book);
  }
  // Other always shows: it is where a new playbook is started.
  return [...groups, other].filter((g) => g.books.length > 0 || g === other);
}

/**
 * The tile's answer to "how big a commitment is this": what finished runs
 * usually cost and took, or — with too few of them — the ceiling a standard
 * run reserves, which the system enforces rather than estimates.
 */
function costLine(
  book: Playbook,
  typical: { runs: number; costUsd: number; seconds: number } | undefined,
  options: Options | undefined,
) {
  if (typical) {
    return (
      <span title={`Median of ${typical.runs} completed runs`}>
        typically <span className="font-mono tabular-nums">{money(typical.costUsd)}</span>{" "}
        and <span className="font-mono tabular-nums">{duration(typical.seconds)}</span>
      </span>
    );
  }
  // Priced by the server at the default model: the playbook's own figure
  // or the depth's, in what a run would actually reserve.
  const ceiling =
    book.ceiling_usd?.standard ??
    options?.depths.find((d) => d.key === "standard")?.default_cost_usd;
  if (ceiling === undefined) return null;
  return (
    <span>
      up to <span className="font-mono tabular-nums">{money(ceiling)}</span> at standard depth
    </span>
  );
}
